import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

interface BarcodeDetectorInstance {
    detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
}
interface BarcodeDetectorConstructor {
    new (options: { formats: string[] }): BarcodeDetectorInstance;
    getSupportedFormats?: () => Promise<string[]>;
}
declare global {
    interface Window {
        BarcodeDetector?: BarcodeDetectorConstructor;
    }
}

export function QrScanner({ onRead }: { onRead: (value: string) => void }) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | undefined>(undefined);
    const [manual, setManual] = useState("");
    const [active, setActive] = useState(false);
    const [error, setError] = useState<string>();
    const stop = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = undefined;
        setActive(false);
    };
    const start = async () => {
        try {
            setError(undefined);
            if (!navigator.mediaDevices?.getUserMedia || !window.BarcodeDetector) {
                setError("Leitura automática indisponível neste navegador. Digite o código abaixo.");
                return;
            }
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" } },
                audio: false,
            });
            streamRef.current = stream;
            setActive(true);
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
        } catch {
            setError("Não foi possível acessar a câmera. Verifique a permissão ou informe o código manualmente.");
        }
    };
    useEffect(() => {
        if (!active || !videoRef.current || !window.BarcodeDetector) return;
        let cancelled = false;
        const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        const scan = async () => {
            if (cancelled || !videoRef.current) return;
            try {
                const [result] = await detector.detect(videoRef.current);
                if (result?.rawValue) {
                    stop();
                    onRead(result.rawValue);
                    return;
                }
            } catch {
                /* continua tentando enquanto a câmera estiver ativa */
            }
            window.setTimeout(() => void scan(), 300);
        };
        void scan();
        return () => {
            cancelled = true;
        };
    }, [active, onRead]);
    useEffect(() => stop, []);
    return (
        <div className="qr-scanner">
            <div className={`camera-frame ${active ? "active" : ""}`}>
                <video ref={videoRef} playsInline muted />
                <span className="scan-corners" />
                {!active ? (
                    <div className="camera-placeholder">
                        <i className="pi pi-qrcode" />
                        <strong>Aponte para o QR do endereço</strong>
                        <small>O sistema confere se você está na posição correta.</small>
                    </div>
                ) : null}
            </div>
            {error ? (
                <p className="scanner-error">
                    <i className="pi pi-info-circle" /> {error}
                </p>
            ) : null}
            <Button
                label={active ? "Fechar câmera" : "Abrir câmera"}
                icon={active ? "pi pi-times" : "pi pi-camera"}
                severity={active ? "secondary" : "success"}
                outlined={active}
                onClick={active ? stop : start}
            />
            <div className="manual-divider">
                <span>ou informe manualmente</span>
            </div>
            <div className="manual-scan">
                <InputText
                    value={manual}
                    onChange={(event) => setManual(event.target.value)}
                    placeholder="Token do endereço"
                />
                <Button
                    icon="pi pi-arrow-right"
                    aria-label="Validar código"
                    disabled={!manual.trim()}
                    onClick={() => onRead(manual.trim())}
                />
            </div>
        </div>
    );
}
